import React, { useEffect } from 'react';
import { Alert, App, Form, Input, Modal } from 'antd';
import { useCancelExpenseMutation } from '../../services/expensesApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatCurrencyLocalized } from '../../utils/currencyHelpers';
import { ExpenseDto, CancelExpenseRequest } from '../../types/expense';

interface Props {
  expense: ExpenseDto | null;
  restaurantId: string;
  onClose: () => void;
}

interface FormValues {
  reason: string;
}

const ExpenseCancelDialog: React.FC<Props> = ({ expense, restaurantId, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  // AntD v5: the static `message` import bypasses the <App> ConfigProvider context, so
  // calls silently no-op in production. App.useApp() returns a contextual instance that
  // actually renders. This is the repo convention (see CashRegisterManagement.tsx:38).
  const { message } = App.useApp();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [cancelExpense, { isLoading: isSaving }] = useCancelExpenseMutation();

  useEffect(() => {
    if (expense) {
      form.resetFields();
    }
  }, [expense, form]);

  if (!expense) return null;

  const hadCashImpact = !!expense.cashMovementId;

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload: CancelExpenseRequest = { reason: values.reason.trim() };

    try {
      await cancelExpense({ restaurantId, id: expense.id, data: payload }).unwrap();
      message.success('Gasto cancelado');
      onClose();
    } catch (err: any) {
      const serverMessage = err?.data?.message;
      // 409 specifically means "register no longer Open" or "already cancelled" — the
      // backend message is already specific, surface it verbatim.
      message.error(serverMessage || 'No se pudo cancelar el gasto.');
    }
  };

  return (
    <Modal
      title="Cancelar gasto"
      open={!!expense}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Confirmar cancelación"
      cancelText="Cerrar"
      confirmLoading={isSaving}
      okButtonProps={{ danger: true, disabled: isSaving }}
      destroyOnClose
    >
      {/* Qué se está cancelando — antes solo se pedía el motivo a ciegas */}
      <div className="fin-summary">
        <span>
          <span className="fin-summary__label">Monto</span>
          <span className="fin-money">{formatCurrencyLocalized(expense.amount, 'MXN')}</span>
        </span>
        <span>
          <span className="fin-summary__label">Fecha</span>
          {formatInTimeZone(expense.expenseDate, 'DD MMM')}
        </span>
        <span>
          <span className="fin-summary__label">Categoría</span>
          {expense.categoryName}
        </span>
        {expense.vendor && (
          <span>
            <span className="fin-summary__label">Proveedor</span>
            {expense.vendor}
          </span>
        )}
      </div>

      {hadCashImpact && (
        <Alert
          type="warning"
          showIcon
          message="Este gasto pagó desde caja."
          description={
            <>
              Al cancelar se generará un movimiento de reverso en la caja de{' '}
              <strong>{expense.cashRegisterUserName || 'el cajero original'}</strong>.
              Si la caja ya no está abierta, la cancelación quedará bloqueada y deberás
              solicitar un ajuste autorizado a soporte.
            </>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Form<FormValues> form={form} layout="vertical">
        <Form.Item
          name="reason"
          label="Motivo de cancelación"
          rules={[
            { required: true, message: 'El motivo es obligatorio' },
            { max: 300, message: 'Máximo 300 caracteres' },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={300}
            showCount
            placeholder="Ej. Capturado por error con monto incorrecto"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExpenseCancelDialog;
